import {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ChatForm from './ChatFormCmp';
import UserList from './UserListCmp';
import Title from './TitleCmp';
import ChatUIControls from './ChatUIControlsCmp';
import {AppConfigState} from '../providers/AppConfigProvider';
import {WSState} from '../providers/WebSocketProvider';
import {VideoState} from '../providers/VideoProvider';
import {ModalState} from '../providers/ModalProvider';
import Video from './VideoCmp';

const LoginContainer = styled.div`
  z-index: 1000;
  margin: 0;
  width: 100vw;
  left: 50%;
  transition: transform .3s;
  cubic-bezier(0, .52, 0, 1);
  text-align: center;
  padding: 3rem 1rem;
  overflow: hidden;
  height: 300px;
  position: absolute;
  top: calc(50% - 150px);
  transform: translate(${({ show }) => show ? '-50%, 0%' : '-150%, 0%'});

  @media screen and (min-width: 768px) {
    padding: 1rem calc((100% - 600px) / 2);
    padding-bottom: 3rem;
  }
`;

const ChatCanvas = styled.div`
  z-index: 1000;
  margin: 0;
  height: 50vh;
  width: 100vw;
  text-align: left;
  padding: 1rem;
  transition: transform .3s;
  transform: translate(${({ show }) => show ? '0%, 0%' : '-100%, 0%'});

  @media screen and (min-width: 768px) {
    height: calc(100vh - 300px);
    max-width: 500px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: inherit;
`;

const FixedContainer = styled.div`
  overflow-y: hidden;
  height: inherit;
  width: inherit;
  -webkit-mask-image: -webkit-gradient(linear, left 20%, left top, from(rgba(0,0,0,1)), to(rgba(0,0,0,0)));

  &:hover {
    overflow-y: auto;
  }

  &::-webkit-scrollbar {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: inherit;
  width: inherit;
`;

const MessageBox = styled.div`
  margin: 6px 0;
  padding: 6px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.background.overlay};
`;

const MessageContent = styled.div`
  padding: 0 6px;
  margin: 0 3px 6px 3px;
  font-weight: ${({ bold }) => bold ? '700' : '400'};
`;

const MessageInfo = styled.small`
  font-weight: 400;
`;

const ScrollToBottom = () => {
  const ref = useRef();
  useEffect(() => ref.current.scrollIntoView());
  return (<div ref={ref}></div>)
}
export const ChatRoom = ({handleDisconnect, handleSendMessage, wsState}) => {
  //chat
  const [showUsers, setShowUsers] = useState(false);

  const {state: vState} = useContext(VideoState);

  const isHost = wsState && wsState.isHost ? wsState.isHost : null;
  const users = wsState && wsState.users ? wsState.users : [];
  const inCall = vState && vState.inCall ? vState.inCall : null;

  const handleShowUsers = () => setShowUsers(true);
  const handleHideUsers = () => setShowUsers(false);

  const messageField = {
    name: 'msg', 
    placeholder: 'Type your message here. (140 chars max)', 
    validation: {required: true},
  };

  const {dispatch: modalDispatch} = useContext(ModalState);

  const handleExitOpen = () => {
    modalDispatch({
      type: 'openMessage',
      title: 'Sign Out',
      message: 'Are you sure you want to sign out of this chatroom?',
      buttonText: 'Yes',
      handleClose: handleExitClose,
      handleConfirm: handleConfirm,
    })
  };
  const handleExitClose = () => {
    modalDispatch({ type: 'close' });
  };
  const handleConfirm = () => {
    modalDispatch({ type: 'close' });
    handleDisconnect();
  };

  const handleWarningOpen = () => {
    modalDispatch({
      type: 'openMessage',
      title: 'Sign Out',
      message: 'You cannot sign out of the chatroom while you are in a call.',
      buttonText: 'Got it!',
      handleClose: handleWarningClose,
      handleConfirm: handleWarningClose,
    })
  };
  const handleWarningClose = () => {
    modalDispatch({ type: 'close' });
  };

  return !showUsers ? (
    <>
      <Container>
        <ChatUIControls 
          isHost={isHost} 
          showUsers={showUsers}
          handleUsers={handleShowUsers}
          handleExit={handleExitOpen}
          handleWarning={handleWarningOpen}
        />

        <FixedContainer>
          <MessageContainer showVideo={isHost || inCall}>
            {Object.keys(wsState.msgs).map(key => {
              const data = wsState.msgs[key];
              return (
                <MessageBox key={key}>
                  {data.from !== "system" ? (
                    <MessageContent bold>
                      <MessageInfo>{data.from}: </MessageInfo>
                      {data.msg}
                    </MessageContent>
                  ) : (
                    <MessageContent>{data.msg}</MessageContent>
                  )}
                </MessageBox>
              );
            })}
            <ScrollToBottom />
          </MessageContainer>
        </FixedContainer>

        <ChatForm field={messageField} buttonType="send" onSubmit={handleSendMessage} />
      </Container>
    </>
  ) : (
    <Container>
      <ChatUIControls 
        isHost={isHost} 
        showUsers={showUsers}
        handleUsers={handleHideUsers}
      />

      <Title arrow as="h5">Guests ({users && (users.length)})</Title>

      <UserList users={users} />
    </Container>
  );
};

ChatRoom.propTypes = {
  handleDisconnect: PropTypes.func,
  handleSendMessage: PropTypes.func,
  wsState: PropTypes.object,
};

const ChatCmp = () => {
  const {state: appState, dispatch: appDispatch} = useContext(AppConfigState);
  const click = (e) => {
    if (e.target.querySelector('.card')) {
      appDispatch({type: 'closeChat'});
    }
  };
  
  const {state: wsState, dispatch: wsDispatch} = useContext(WSState);
  const connect = async data => {
    wsDispatch({
      type: 'connect',
      fullName: data.fullName,
    });
  };
  const sendMessage = async data => {
    wsDispatch({
      type: 'send-message',
      msg: data.msg,
    });
  };
  const disconnect = () => {
    wsDispatch({
      type: 'disconnect',
    });
  };

  const connectField = {
    name: 'fullName', 
    placeholder: 'Enter your name to get started.', 
    validation: {required: true},
  };

  return (
    wsState && !wsState.connectionId ? (
      <LoginContainer onClick={click} show={appState.chatIsVisible}>
        <Title as="h3" arrow secondary>Join the conversation</Title>
        <ChatForm field={connectField} buttonType="enter" onSubmit={connect} />
      </LoginContainer>
    ): (
      <ChatCanvas onClick={click} show={appState.chatIsVisible}>
        <Video />

        <ChatRoom handleDisconnect={disconnect} handleSendMessage={sendMessage} wsState={wsState} />
      </ChatCanvas>
    )
  );
};

ChatCmp.propTypes = {
  children: PropTypes.node,
};

export default ChatCmp;
